/**
 * An anonymous farm worker: coveralls, rubber boots, cap and mask, but nothing
 * that could turn one of the corridor's three men into a character of his own.
 */
import * as THREE from 'three';
import { collapseRigged } from '@/world/instancing';
import { surface } from '../materials';
import type { AssetDefinition } from '../types';

function part(geometry: THREE.BufferGeometry, material: THREE.Material, name: string): THREE.Mesh {
  const mesh = new THREE.Mesh(geometry, material);
  mesh.name = name;
  mesh.castShadow = true;
  return mesh;
}

/**
 * How far back the boots sit from the hip's own axis, in metres.
 *
 * **This is the asset contract's Z-centring rule, and it failed here exactly the
 * way `worker-legs.ts` records it failing.** A leg is not symmetric about its
 * ankle: the toe reaches 0.23 forward while the deepest thing behind the axis is
 * the shoulder yoke at 0.145, so a boot placed on the axis puts the whole figure
 * 42 mm forward of its own origin and `asset.spec.ts` rejects it at 30 mm.
 *
 * 0.05 brings the bounds to −0.145 … 0.18 — an 18 mm offset, inside the gate
 * with room on both sides — and it costs nothing anatomically, because an ankle
 * sitting fractionally behind the hip with the toe ahead of it is where an ankle
 * actually is.
 */
const BOOT_BACK = 0.05;

/** Hip height: the waist pivot, and the height both legs hang from. */
const WAIST_Y = 0.92;

/**
 * Beat 1 authored these boots close enough to see, so the whole worker carries
 * their geometry unchanged. A corridor figure with a different toe or sole
 * would make the same man change shoes between adjacent beats.
 */
function addBoot(
  group: THREE.Group,
  side: 'left' | 'right',
  bootMaterial: THREE.Material,
  soleMaterial: THREE.Material,
): void {
  const sole = part(new THREE.BoxGeometry(0.125, 0.012, 0.28), soleMaterial, `${side}-boot-sole`);
  sole.position.set(0, -0.914, 0.09 - BOOT_BACK);
  group.add(sole);

  const foot = part(new THREE.BoxGeometry(0.115, 0.055, 0.21), bootMaterial, `${side}-boot-foot`);
  foot.position.set(0, -0.8805, 0.055 - BOOT_BACK);
  group.add(foot);

  const toe = part(new THREE.SphereGeometry(1, 8, 4), bootMaterial, `${side}-boot-rounded-toe`);
  toe.scale.set(0.0575, 0.0275, 0.07);
  toe.position.set(0, -0.8805, 0.16 - BOOT_BACK);
  group.add(toe);

  const shaft = part(new THREE.CylinderGeometry(0.085, 0.075, 0.345, 12), bootMaterial, `${side}-boot-shaft`);
  shaft.position.set(0, -0.6925, -BOOT_BACK);
  group.add(shaft);
}

function leg(
  side: 'left' | 'right',
  x: number,
  coverall: THREE.Material,
  boot: THREE.Material,
  sole: THREE.Material,
): THREE.Group {
  const hip = new THREE.Group();
  hip.name = `worker-hip-${side}`;
  hip.position.set(x, 0.92, 0);

  const upper = part(new THREE.CylinderGeometry(0.1, 0.115, 0.42, 12), coverall, `${side}-upper-leg`);
  upper.position.set(0, -0.21, 0);
  hip.add(upper);

  const fold = part(new THREE.CylinderGeometry(0.102, 0.102, 0.07, 12), coverall, `${side}-knee-fold`);
  fold.position.set(0, -0.42, 0);
  hip.add(fold);

  const lower = part(new THREE.CylinderGeometry(0.085, 0.095, 0.4, 12), coverall, `${side}-lower-leg`);
  lower.position.set(0, -0.63, 0);
  hip.add(lower);
  addBoot(hip, side, boot, sole);
  return hip;
}

function arm(side: 'left' | 'right', x: number, coverall: THREE.Material, boot: THREE.Material): THREE.Group {
  const shoulder = new THREE.Group();
  shoulder.name = `worker-shoulder-${side}`;
  shoulder.position.set(x, 1.44, 0);

  const upper = part(new THREE.CylinderGeometry(0.055, 0.062, 0.3, 12), coverall, `${side}-upper-arm`);
  upper.position.set(0, -0.15, 0);
  shoulder.add(upper);

  const forearm = part(new THREE.CylinderGeometry(0.048, 0.055, 0.28, 12), coverall, `${side}-forearm`);
  forearm.position.set(0, -0.44, 0);
  shoulder.add(forearm);

  const glove = part(new THREE.BoxGeometry(0.09, 0.15, 0.075), boot, `${side}-glove`);
  glove.position.set(0, -0.65, 0);
  shoulder.add(glove);
  return shoulder;
}

function build(): THREE.Group {
  const group = new THREE.Group();
  group.name = 'worker';
  // Four values and one surface kind keep a crowd of workers within the draw-call
  // budget; adding a buckle or trim colour here multiplies across every patrol.
  const coverall = surface('organic', 0x3c4550);
  const boot = surface('organic', 0x242c2a);
  const sole = surface('organic', 0x15191a);
  // A value down from the near-white it started at. It has to be the brightest
  // thing on him and it must not be brighter than the concrete he stands on.
  const mask = surface('organic', 0xa8a49b);

  const hipLeft = leg('left', -0.12, coverall, boot, sole);
  const hipRight = leg('right', 0.12, coverall, boot, sole);
  group.add(hipLeft, hipRight);

  // Everything above the hips hangs from one pivot at hip height, so he can
  // bend at the waist: beat 1's worker reaches into a pen through its open
  // gate (beat 1 revision 2, C). The parts keep their standing positions;
  // only their parent moved, so the bind pose and the silhouette are
  // unchanged, and `collapseRigged` merges the waist's own parts the way it
  // merged them into the root before.
  const waist = new THREE.Group();
  waist.name = 'worker-waist';
  waist.position.set(0, WAIST_Y, 0);
  group.add(waist);
  const upper = (mesh: THREE.Object3D): void => {
    mesh.position.y -= WAIST_Y;
    waist.add(mesh);
  };

  const shoulderLeft = arm('left', -0.255, coverall, boot);
  const shoulderRight = arm('right', 0.255, coverall, boot);
  upper(shoulderLeft);
  upper(shoulderRight);

  const torso = part(new THREE.BoxGeometry(0.44, 0.52, 0.27), coverall, 'worker-torso');
  torso.position.set(0, 1.19, 0);
  upper(torso);
  // 0.25 deep against the torso's 0.27, and the first build had it at 0.29.
  // Wider than the torso is the point — it is what gives him shoulders — but
  // DEEPER than the torso made it a shelf standing proud front and back, and a
  // flat shelf across the top of a boxy chest is the silhouette of body armour.
  // These are men who work here, not men who have come for her.
  const yoke = part(new THREE.BoxGeometry(0.5, 0.09, 0.25), coverall, 'worker-shoulder-yoke');
  yoke.position.set(0, 1.47, 0);
  upper(yoke);

  const neck = part(new THREE.CylinderGeometry(0.055, 0.055, 0.1, 12), coverall, 'worker-neck');
  neck.position.set(0, 1.5, 0);
  upper(neck);

  const head = part(new THREE.SphereGeometry(1, 12, 8), coverall, 'worker-hooded-head');
  head.scale.set(0.105, 0.125, 0.115);
  head.position.set(0, 1.655, 0);
  upper(head);
  const peak = part(new THREE.BoxGeometry(0.19, 0.022, 0.09), coverall, 'worker-cap-peak');
  peak.position.set(0, 1.675, 0.1);
  upper(peak);
  // Wider and shorter than the first build, and a value down from it.
  //
  // At 0.125 × 0.075 in a near-white it read as a set of bared teeth in a dark
  // face — which is a completely different character from the one this figure is
  // for. A mask covers the lower face corner to corner, so widening it past the
  // head's own width and flattening it is both more truthful and reads as cloth.
  // It is still the only value break above the waist and still the only thing on
  // him a player's eye will go to; it just no longer looks like an expression.
  const faceMask = part(new THREE.BoxGeometry(0.15, 0.058, 0.045), mask, 'worker-mask');
  faceMask.position.set(0, 1.6, 0.08);
  upper(faceMask);

  collapseRigged(group, [hipLeft, hipRight, waist, shoulderLeft, shoulderRight]);

  return group;
}

export interface WorkerParts {
  hips: readonly [THREE.Object3D, THREE.Object3D];
  /** Everything above the hips. `rotation.x` > 0 bends him forward. */
  waist: THREE.Object3D;
  shoulders: readonly [THREE.Object3D, THREE.Object3D];
}

function required(root: THREE.Object3D, name: string): THREE.Object3D {
  const found = root.getObjectByName(name);
  if (!found) throw new Error(`worker: rig node "${name}" missing — was the mesh renamed?`);
  return found;
}

export function findWorkerParts(root: THREE.Object3D): WorkerParts {
  return {
    hips: [required(root, 'worker-hip-left'), required(root, 'worker-hip-right')],
    waist: required(root, 'worker-waist'),
    shoulders: [required(root, 'worker-shoulder-left'), required(root, 'worker-shoulder-right')],
  };
}

export const workerAsset: AssetDefinition = {
  id: 'worker',
  label: 'Worker',
  description: 'Anonymous coverall worker with a pale mask and no identifying detail.',
  // Measured off the built geometry, not taken from a brief — same discipline as
  // `worker-legs.ts`. Width is the two arms (±0.317), not the yoke; length is the
  // boot toe forward against the yoke behind, after `BOOT_BACK`; height is the
  // crown of the cap.
  footprint: { width: 0.634, height: 1.78, length: 0.325 },
  create: build,
};
